import React from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { formatCamelCase } from "../../../../shared/utils/formatter";
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
} from "@mui/icons-material";
import {
  State,
  Action,
  KPIOption,
  ActionType,
  StayDateConfig,
} from "../../../../shared/interfaces/kpi";
import { hasSubmenus } from "../../../../shared/utils/kpi";

interface KpiConfigPanelProps {
  state: State;
  dispatch: React.Dispatch<Action>;
  currentKPISettings: KPIOption;
  selectedPresetName: string;
  isApplyDisabled: boolean;
  handleApplyChanges: () => void;
  handleRemoveKpi: () => void;
}

const KpiConfigPanel: React.FC<KpiConfigPanelProps> = ({
  state,
  dispatch,
  currentKPISettings,
  selectedPresetName,
  isApplyDisabled,
  handleApplyChanges,
  handleRemoveKpi,
}) => {
  const theme = useTheme();

  const handleToggle = (field: "segments" | "comparison", value: string) => {
    const arr = (state.editingKPI[field] as string[]) || [];
    dispatch({
      type: ActionType.UPDATE_EDITING_KPI,
      updates: {
        [field]: arr.includes(value)
          ? arr.filter((v: string) => v !== value)
          : [...arr, value],
      },
    });
  };

  const handleStayDateChange = (updates: Partial<StayDateConfig>) => {
    dispatch({
      type: ActionType.UPDATE_SHARED_STAY_DATE,
      stayDate: { ...state.sharedStayDate, ...updates },
    });
  };

  const renderList = (
    obj: Record<string, boolean>,
    field: "segments" | "comparison"
  ) =>
    field === "segments" ? (
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        {Object.entries(obj)
          .filter(([_, v]) => v)
          .map(([key]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={(
                    state.editingKPI[field] as string[] | undefined
                  )?.includes(key)}
                  onChange={() => handleToggle(field, key)}
                />
              }
              label={formatCamelCase(key).toLowerCase()}
              sx={{ width: "50%", m: 0 }}
            />
          ))}
      </Box>
    ) : (
      <FormGroup>
        {Object.entries(obj)
          .filter(([_, v]) => v)
          .map(([key]) => (
            <FormControlLabel
              key={key}
              control={
                <Checkbox
                  checked={(
                    state.editingKPI[field] as string[] | undefined
                  )?.includes(key)}
                  onChange={() => handleToggle(field, key)}
                />
              }
              label={formatCamelCase(key).toLowerCase()}
            />
          ))}
      </FormGroup>
    );

  const isKeyPresentInPreset = state.editingKPIKey
    ? state.editingKPIKey in
      state.savedConfigurations[state.selectedConfigId].kpiSettings
    : false;

  return (
    <Paper sx={{ p: { xs: 1, md: 3 }, borderRadius: "12px" }}>
      <Typography
        variant="h6"
        color={theme.palette.primary.main}
        fontWeight="bold"
      >
        {formatCamelCase(state.editingKPIKey!)} Settings
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 3, display: "block" }}
      >
        Configure this KPI for {selectedPresetName}. (Date Range:{" "}
        {state.editingPresetDate
          ? formatCamelCase(state.editingPresetDate)
          : "Not Set"}
        )
      </Typography>

      {!hasSubmenus(currentKPISettings) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          This KPI is non-configurable. Use the <b>Apply</b> button below to
          include it in the preset, or the <b>Remove</b> button to exclude it.
        </Alert>
      )}

      {Object.entries(currentKPISettings)
        .filter(([key]) => key !== "date" && key !== "isActive")
        .map(([key, value]) => {
          if (!hasSubmenus(currentKPISettings) && key !== "stayDate")
            return null;

          if (key === "segment" || key === "comparison") {
            return (
              <FormControl key={key} fullWidth sx={{ mb: 3, display: "block" }}>
                <FormLabel>{formatCamelCase(key)}</FormLabel>
                {renderList(
                  value as Record<string, boolean>,
                  key === "segment" ? "segments" : "comparison"
                )}
              </FormControl>
            );
          }

          if (key === "stayDate") {
            const stay = state.sharedStayDate;
            return (
              <FormControl key={key} fullWidth sx={{ mb: 3, display: "block" }}>
                <FormLabel>STAY DATE</FormLabel>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    mt: 2,
                    p: 2,
                    bgcolor: "grey.100",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.secondary" }}
                  >
                    Stay Date Configuration
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="stay-type-label">Type</InputLabel>
                        <Select
                          labelId="stay-type-label"
                          value={stay.type}
                          label="Type"
                          sx={{
                            minHeight: 36,
                            ".MuiSelect-select": {
                              display: "flex",
                              alignItems: "center",
                              height: "100%",
                            },
                          }}
                          onChange={(e) =>
                            handleStayDateChange({
                              type: e.target.value as StayDateConfig["type"],
                            })
                          }
                        >
                          <MenuItem value="day">Day</MenuItem>
                          <MenuItem value="week">Week</MenuItem>
                          <MenuItem value="month">Month</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <TextField
                        type="number"
                        fullWidth
                        size="small"
                        label="Value"
                        value={stay.value === null ? "" : stay.value}
                        onChange={(e) =>
                          handleStayDateChange({
                            value:
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                          })
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              </FormControl>
            );
          }

          // Handle boolean options (if any)
          if (typeof value === "boolean") {
            return (
              <FormControl key={key} fullWidth sx={{ mb: 3, display: "block" }}>
                <FormLabel>{formatCamelCase(key)}</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={(state.editingKPI as any)[key] === true}
                        onChange={(e) =>
                          dispatch({
                            type: ActionType.UPDATE_EDITING_KPI,
                            updates: { [key]: e.target.checked },
                          })
                        }
                      />
                    }
                    label={formatCamelCase(key)}
                  />
                </FormGroup>
              </FormControl>
            );
          }
          return null;
        })}

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mt: 3,
          mb: { xs: 6, md: 0 },
          pt: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {isKeyPresentInPreset && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={handleRemoveKpi}
          >
            Remove KPI
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={handleApplyChanges}
          disabled={isApplyDisabled}
        >
          Apply Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default KpiConfigPanel;
